<script setup>
import { onMounted, watch, ref, onUnmounted } from "vue";
import Sidebar from "../components/Sidebar.vue";
import TopBar from "../components/TopBar.vue";
import AnalyticsFilters from "../components/analytics/AnalyticsFilters.vue";
import AnalyticsKPIs from "../components/analytics/AnalyticsKPIs.vue";
import AnalyticsDonuts from "../components/analytics/AnalyticsDonuts.vue";
import AnalyticsSeries from "../components/analytics/AnalyticsSeries.vue";
import AnalyticsTables from "../components/analytics/AnalyticsTables.vue";
import AnalyticsPredictions from "../components/analytics/AnalyticsPredictions.vue";
import ChatAssistant from "../components/ChatAssistant.vue";

import { useAnalyticsData } from "../composables/useAnalyticsData.js";
import { useAnalyticsAI } from "../composables/useAnalyticsAI.js";
import { formatMoney } from "../utils/analyticsUtils.js";

// Sidebar state
const sidebarCollapsed = ref(false);

function toggleSidebar() {
  sidebarCollapsed.value = !sidebarCollapsed.value;
}

// Composables
const {
  periodo,
  activeRange,
  advStart,
  advEnd,
  advMethods,
  advMin,
  advMax,
  advCustomer,
  advProduct,
  advSupplier,
  payMethodOptions,
  kpis,
  clientsCount,
  ordersCount,
  salesDaily,
  salesMonthly,
  salesYearly,
  filteredSalesDetails,
  filteredPurchasesDetails,
  maxSalesDaily,
  maxSalesMonthly,
  maxSalesYearly,
  loadPaymentMethods,
  loadSalesSeries,
  loadAnalytics,
  loadDetailedSales,
  loadDetailedPurchases,
} = useAnalyticsData();

const {
  forecastDaily,
  forecastMonthly,
  maxForecastDaily,
  maxForecastMonthly,
  aiText,
  aiLoading,
  aiHtml,
  generateForecast,
  generateAIInsights,
} = useAnalyticsAI();

// AI Section State
const showAiPanel = ref(true);

function closeAiPanel() {
  showAiPanel.value = false;
}

// Live Mode State
const isLive = ref(false);
let liveInterval = null;

function toggleLiveMode() {
  isLive.value = !isLive.value;
  if (isLive.value) {
    loadAll(); // Immediate refresh
    liveInterval = setInterval(() => {
      loadAll();
    }, 30000); // Refresh every 30s
  } else {
    if (liveInterval) clearInterval(liveInterval);
    liveInterval = null;
  }
}

onUnmounted(() => {
  if (liveInterval) clearInterval(liveInterval);
  try {
    const h = (window).__opsSaleHandler;
    if (h) window.removeEventListener('ops:sale:created', h);
    (window).__opsSaleHandler = null;
  } catch { }
});

// Tooltip State
const hoveredPoint = ref(null);
const tooltipStyle = ref({});

function showTooltip(e, data, labelPrefix = "") {
  const rect = e.target.getBoundingClientRect();
  hoveredPoint.value = {
    label: labelPrefix + (data.label || new Date(data.date).toLocaleDateString()),
    value: formatMoney(data.amount || data.total || 0),
  };
  tooltipStyle.value = {
    top: `${rect.top - 40}px`,
    left: `${rect.left + rect.width / 2}px`,
  };
}

function hideTooltip() {
  hoveredPoint.value = null;
}

// Lifecycle
onMounted(async () => {
  try {
    await loadPaymentMethods();
  } catch (e) {
    console.error("loadPaymentMethods failed:", e);
  }

  try {
    await loadAll();
  } catch (e) {
    console.error("loadAll failed:", e);
  }
  try {
    const handler = async () => {
      await loadAll();
    };
    window.addEventListener('ops:sale:created', handler);
    (window).__opsSaleHandler = handler;
  } catch { }

  // Init KPI animations
  animateNumber(animClients, Number(clientsCount.value || 0));
  animateNumber(animIncome, Number(kpis.value?.ingresos || 0));
  animateNumber(animOrders, Number(ordersCount.value || 0));
});

// Watchers
watch([periodo, advStart, advEnd], () => {
  loadAll();
});

watch([advMethods, advMin, advMax, advCustomer, advSupplier, advProduct], () => {
  loadDetailedSales();
  loadDetailedPurchases();
});

watch(() => clientsCount.value, (nv) => animateNumber(animClients, Number(nv || 0)));
watch(() => ordersCount.value, (nv) => animateNumber(animOrders, Number(nv || 0)));
watch(() => kpis.value?.ingresos, (nv) => animateNumber(animIncome, Number(nv || 0)));

async function loadAll() {
  try {
    await Promise.all([
      loadAnalytics(),
      loadSalesSeries(),
      loadDetailedSales(),
      loadDetailedPurchases(),
    ]);
  } catch (e) {
    console.error("loadAll error:", e);
  }
}

async function handleGenerateAi() {
  showAiPanel.value = true;
  await generateForecast(salesDaily.value, salesMonthly.value, salesYearly.value);
  await generateAIInsights(
    kpis.value,
    filteredSalesDetails.value,
    filteredPurchasesDetails.value,
    [],
    activeRange()
  );
}
</script>

<template>
  <div class="layout">
    <Sidebar :collapsed="sidebarCollapsed" />
    <div class="main-content">
      <TopBar title="Dashboard" @toggle-sidebar="toggleSidebar">
        <template #actions>
          <button class="live-btn" :class="{ active: isLive }" @click="toggleLiveMode">
            <span class="material-symbols-rounded icon">sensors</span>
            {{ isLive ? 'En Vivo' : 'Live Mode' }}
            <span v-if="isLive" class="pulse-dot"></span>
          </button>
        </template>
      </TopBar>

      <div class="dashboard-container">
        <!-- Header & KPIs -->
        <AnalyticsKPIs :kpis="kpis" :clients-count="clientsCount" :orders-count="ordersCount" v-model:periodo="periodo"
          :animated-clients="animClients" :animated-income="animIncome" :animated-orders="animOrders" />

        <!-- Filters -->
        <AnalyticsFilters v-model:start="advStart" v-model:end="advEnd" v-model:methods="advMethods"
          v-model:min="advMin" v-model:max="advMax" v-model:customer="advCustomer" v-model:supplier="advSupplier"
          v-model:product="advProduct" :method-options="payMethodOptions" @generateAi="handleGenerateAi" @clear="() => {
            advStart = '';
            advEnd = '';
            advMethods = [];
            advMin = '';
            advMax = '';
            advCustomer = '';
            advSupplier = '';
            advProduct = '';
            loadAll();
          }" />

        <!-- AI Insights -->
        <section v-if="(aiLoading || aiText) && showAiPanel" class="panel ai-panel">
          <div class="panel-head">
            <b>
              <span class="material-symbols-rounded ai-icon">auto_awesome</span>
              Análisis IA & Pronósticos
            </b>
            <button class="close-btn" @click="closeAiPanel" aria-label="Cerrar análisis">
              <span class="material-symbols-rounded">close</span>
            </button>
          </div>
          <div v-if="aiLoading" class="ai-loading">
            <div class="spinner"></div>
            <span>Generando insights y pronósticos...</span>
          </div>
          <div v-else class="ai-content">
            <div class="ai-text" v-html="aiHtml"></div>

            <!-- Forecast Charts -->
            <div v-if="forecastDaily.length" class="forecast-grid">
              <div class="forecast-card">
                <div class="f-title">Pronóstico 7 días</div>
                <div class="f-bars">
                  <div v-for="d in forecastDaily" :key="d.date" class="f-col">
                    <div class="f-bar" :style="{
                      height: (d.amount / maxForecastDaily) * 60 + 'px',
                    }" @mouseenter="showTooltip($event, d)" @mouseleave="hideTooltip"></div>
                    <div class="f-label">{{ new Date(d.date).getDate() }}</div>
                  </div>
                </div>
              </div>
              <div class="forecast-card">
                <div class="f-title">Pronóstico 6 meses</div>
                <div class="f-bars">
                  <div v-for="m in forecastMonthly" :key="m.month" class="f-col">
                    <div class="f-bar" :style="{
                      height: (m.amount / maxForecastMonthly) * 60 + 'px',
                    }" @mouseenter="showTooltip($event, m, '')" @mouseleave="hideTooltip"></div>
                    <div class="f-label">{{ m.month }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Real-time Predictions -->
        <AnalyticsPredictions :daily="salesDaily" :monthly="salesMonthly" :yearly="salesYearly" />

        <!-- Charts Grid -->
        <div class="viz-section">
          <AnalyticsDonuts :daily="salesDaily" :monthly="salesMonthly" :yearly="salesYearly" />
          <div class="series-col">
            <AnalyticsSeries :daily="salesDaily" :monthly="salesMonthly" :yearly="salesYearly"
              :max-daily="maxSalesDaily" :max-monthly="maxSalesMonthly" :max-yearly="maxSalesYearly" />
          </div>
        </div>

        <!-- Detailed Tables -->
        <AnalyticsTables :sales="filteredSalesDetails" :purchases="filteredPurchasesDetails" />
      </div>
    </div>

    <!-- Global Tooltip -->
    <div v-if="hoveredPoint" class="chart-tooltip" :style="tooltipStyle">
      <div class="tt-label">{{ hoveredPoint.label }}</div>
      <div class="tt-val">{{ hoveredPoint.value }}</div>
    </div>

    <ChatAssistant />
  </div>
</template>

<style scoped>
.layout {
  display: flex;
  height: 100vh;
  background: var(--bg);
  font-family: "Inter", sans-serif;
  color-scheme: light dark;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.dashboard-container {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  background: linear-gradient(135deg, #0a0b14 0%, #1a1610 100%);
  min-height: 100vh;
}

.panel {
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 16px;
  padding: 1rem;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  color: var(--text);
}

.close-btn {
  background: transparent;
  border: none;
  color: var(--muted);
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  color: var(--text);
}

.viz-section {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

/* KPI Cards color variants */
:deep(.kpis-cards .soft-card:nth-child(1)) {
  background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
  border-color: #ea580c;
}

:deep(.kpis-cards .soft-card:nth-child(2)) {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-color: #059669;
}

:deep(.kpis-cards .soft-card:nth-child(3)) {
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  border-color: #4f46e5;
}

/* Badges tint to match card theme */
:deep(.kpis-cards .soft-card:nth-child(2) .card-badge) {
  background: #059669;
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.25);
}

:deep(.kpis-cards .soft-card:nth-child(3) .card-badge) {
  background: #4f46e5;
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(79, 70, 229, 0.25);
}

@media (min-width: 1100px) {
  .viz-section {
    grid-template-columns: 1fr 1fr;
  }
}

.series-col {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* AI Styles */
.ai-panel {
  border: 1px solid rgba(106, 92, 245, 0.3);
  background: var(--surface);
}

.ai-icon {
  color: var(--accent);
  margin-right: 6px;
}

.ai-loading {
  display: flex;
  align-items: center;
  gap: 1rem;
  color: var(--muted);
  padding: 1rem;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 3px solid var(--border);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.ai-content {
  padding: 0.5rem;
}

.ai-text {
  margin-bottom: 1.5rem;
  line-height: 1.6;
  color: var(--text);
  white-space: pre-wrap;
  /* Fix text truncation */
  word-break: break-word;
}

.forecast-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

@media (min-width: 768px) {
  .forecast-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.forecast-card {
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 1rem;
  background: var(--surface);
}

.f-title {
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: var(--accent);
}

.f-bars {
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  height: 80px;
  gap: 4px;
}

.f-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.f-bar {
  width: 100%;
  background: linear-gradient(180deg, var(--accent) 0%, rgba(106, 92, 245, 0.6) 100%);
  border-radius: 4px 4px 0 0;
  min-height: 2px;
  transition: opacity 0.2s;
  cursor: pointer;
}

.f-bar:hover {
  opacity: 0.8;
}

.f-label {
  font-size: 0.7rem;
  color: var(--muted);
  font-weight: 600;
}

/* Tooltip */
.chart-tooltip {
  position: fixed;
  transform: translate(-50%, -100%);
  background: rgba(15, 23, 42, 0.9);
  color: #fff;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 0.8rem;
  pointer-events: none;
  z-index: 100;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(4px);
}

.tt-label {
  font-weight: 600;
  margin-bottom: 2px;
  color: #cbd5e1;
}

.tt-val {
  font-weight: 700;
  color: #fff;
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .layout {
    color-scheme: dark;
  }

  .panel {
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  }

  .spinner {
    border-color: #334155;
    border-top-color: var(--accent);
  }

  .close-btn:hover {
    background: rgba(255, 255, 255, 0.1);
  }
}

/* Light mode support */
@media (prefers-color-scheme: light) {
  .layout {
    color-scheme: light;
  }
}

:root[data-theme="dark"] .layout {
  color-scheme: dark;
}

:root[data-theme="light"] .layout {
  color-scheme: light;
}

/* Live Mode Button */
.live-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--muted);
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.live-btn .icon {
  font-size: 18px;
}

.live-btn:hover {
  background: var(--bg);
  color: var(--text);
}

.live-btn.active {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border-color: rgba(239, 68, 68, 0.2);
}

.pulse-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ef4444;
  box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
  animation: live-pulse 2s infinite;
}

@keyframes live-pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
  }

  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(239, 68, 68, 0);
  }

  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .dashboard-container {
    padding: 1rem;
    gap: 1rem;
  }

  .panel {
    padding: 0.85rem;
  }

  .ai-panel {
    padding: 0.75rem;
  }

  .forecast-grid {
    gap: 0.75rem;
  }

  .forecast-card {
    padding: 0.75rem;
  }

  .f-bars {
    height: 60px;
    gap: 3px;
  }

  .f-label {
    font-size: 0.65rem;
  }

  .live-btn {
    font-size: 0.8rem;
    padding: 5px 10px;
    gap: 6px;
  }

  .live-btn .icon {
    font-size: 16px;
  }
}

@media (max-width: 480px) {
  .dashboard-container {
    padding: 0.75rem;
    gap: 0.75rem;
  }

  .panel {
    padding: 0.75rem;
    border-radius: 12px;
  }

  .panel-head {
    font-size: 0.9rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .panel-head b {
    font-size: 1rem;
  }

  .ai-panel {
    padding: 0.65rem;
  }

  .ai-text {
    font-size: 0.85rem;
    margin-bottom: 1rem;
  }

  .forecast-grid {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .forecast-card {
    padding: 0.65rem;
  }

  .f-title {
    font-size: 0.85rem;
    margin-bottom: 0.5rem;
  }

  .f-bars {
    height: 50px;
    gap: 2px;
  }

  .f-label {
    font-size: 0.6rem;
  }

  .live-btn {
    font-size: 0.75rem;
    padding: 4px 8px;
    gap: 5px;
  }

  .live-btn .icon {
    font-size: 14px;
  }

  .chart-tooltip {
    font-size: 0.75rem;
    padding: 5px 8px;
  }

  .viz-section {
    gap: 1rem;
  }
}
</style>
// KPI animations
const animClients = ref(0);
const animIncome = ref(0);
const animOrders = ref(0);

function animateNumber(outRef, to, duration = 800) {
const from = Number(outRef.value || 0);
const start = performance.now();
function step(now) {
const t = Math.min(1, (now - start) / duration);
const eased = 1 - Math.pow(1 - t, 3);
outRef.value = from + (to - from) * eased;
if (t < 1) requestAnimationFrame(step); } requestAnimationFrame(step); }